import React, { useCallback, useContext, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import { Theme } from '@mui/material';
import { BusinessDataFormContext } from '../BusinessDataForm';
import {
  InternalError,
  EDErrorTimeStamp,
  ErrorText,
} from '../../../../../../../Components/InternalError/InternalError';
import DialogScrollOverlay from '../../../../../../../Components/DialogScrollOverlay/DialogScrollOverlay';
import Dialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import BusinessDataFormEditMenuActionButton from '../BusinessDataFormEditMenuActionButton/BusinessDataFormEditMenuActionButton';
import {
  defaultEditMenuCancelButton,
  defaultEditMenuSaveButton,
  splitEditMenuActions,
  IEditMenuAction,
} from '../Utils/BusinessDataFormUtils';
import FloatingMenu, {
  FloatingMenuButton,
  FloatingMenuButtonLabel,
  FloatingMenuSlot,
  useIsFloatingMenuVisible,
} from '../../../../../../../Components/FloatingBottomArea/FloatingMenu';
import MenuButton from '../../../../../../../BaseComponents/Buttons/MenuButton/EDMenuButton';
import EDLabelMenu from '../../../../../../../BaseComponents/LocalMenu/EDLabelMenu';
import EDConfirmationDialog from '../../../../../../../BaseComponents/ConfirmationDialog/EDConfirmationDialog';
import FunctionItem from '../../../FunctionMenu/FunctionItem/FunctionItem';
import EDIcon from '../../../../../../../BaseComponents/Icon/EDIcon';
import { MdDetailSelectionContext } from '../../../../../../../Components/MasterDetail/Contexts/MdDetailSelectionContext';
import { useWidth } from '../../../../../../../Components/BreakpointContext/BreakpointContextUtils';
import backgroundImage from '../../../../../../../Assets/Backgrounds/errorBackground.svg';
import { EDControlledExpansionPanel } from '../../../../../../../BaseComponents/Accordion/EDExpansionPanel';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import { ObjektVyContext } from '../../ObjektVy/ObjektVy';
import BusinessDataFormEditMenuAdjustSideWidths from '../Components/BusinessDataFormEditMenuAdjustSideWidths/BusinessDataFormEditMenuAdjustSideWidths';
import { useRollbarId } from '../../../../../../../Components/InternalError/RollbarIdContext';
import { findRoutineDetaljVyCollectionName } from '../../../../Const/Rutiner';
import { themeColors } from '../../../../../../../BaseComponents/ThemeProvider/AvenyThemeColors';
import { IFunctionMenuItem } from '../../../../../../../Utils/ObjektVyUtils';
import { useVisningstypContext } from '../../VisningsTypContext/VisningsTypContext';
interface IProps {
  onCancel?: () => void;
  missingFormvalue?: (childData: boolean) => void;
  directEditMode?: boolean;
  rutinKod: string | undefined;
  functionMenuItems?: IFunctionMenuItem[];
}

const useStyles = makeStyles()((theme: Theme) => ({
  overlay: {
    padding: theme.spacing(0, 3),
    borderTop: `1px solid ${theme.customProperties.Dialog.actionDivider}`,
    height: `${theme.customProperties.businessDataFormEditMenuHeight}px`,
    flex: 'none',
    boxSizing: 'border-box',
    background: theme.customProperties.Dialog.actionsBg,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0),
    },
  },
  overlayNoPaddingXS: {
    [theme.breakpoints.down('sm')]: {
      padding: 0,
    },
  },
  secondaryButton: {
    marginRight: 16,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.common.white,
    },
  },
  iconPadding: {
    paddingRight: 8,
  },
  actionButtonsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '8px 0px',
    width: '100%',
    minHeight: '100%',
    boxSizing: 'border-box',
    overflowX: 'auto',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0.5, 1),
      justifyContent: 'stretch',
      "& [title^='Mer'] + button[class*='MuiButton-text']": {
        margin: 0,
      },
    },
  },
  actionButtonsContainerSpaceAroundXS: {
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'space-around',
      overflowX: 'unset',
    },
  },

  validationErrorTooltipRow: {
    margin: 0,
  },
  dialogFooterContent: {
    display: 'flex',
    padding: '8px 0px',
    justifyContent: 'flex-end',
  },
  errorMessageContainer: {
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
    overflowY: 'scroll',
    minHeight: 600,
  },
  dialogPaper: {
    backgroundImage: 'url("' + backgroundImage + '")',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: '20% 90%',
    [theme.breakpoints.up('lg')]: {
      minWidth: 600,
      width: 600,
      minHeight: 600,
    },
  },
  content: {
    [theme.breakpoints.up('lg')]: {
      overflowX: 'hidden',
      overflowY: 'scroll',
      maxHeight: 600,
      maxWidth: '100%',
    },
    [theme.breakpoints.up('md')]: {
      overflowX: 'hidden',
      overflowY: 'scroll',
      minHeight: 600,
      maxWidth: '100%',
    },
    overflowWrap: 'break-word',
  },
  timestamp: {
    background: theme.customProperties.errorComponent.timestampError,
    position: 'absolute',
    bottom: theme.spacing(6.75),
    right: theme.spacing(0),
    padding: theme.spacing(1),
    borderTopLeftRadius: theme.spacing(0.65),
  },
  errorTextExpanded: {
    fontStyle: 'italic',
    margin: 'auto',
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  errorText: {
    textOverflow: 'ellipsis' /* enables ellipsis */,
    whiteSpace: 'nowrap' /* keeps the text in a single line */,
    overflow: 'hidden',
    fontStyle: 'italic',
    margin: 'auto',
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
}));

export const BusinessDataFormEditMenu: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes, cx } = useStyles();
  const formContext = useContext(BusinessDataFormContext);
  const detailSelectionContext = useContext(MdDetailSelectionContext);
  const objektVyContext = useContext(ObjektVyContext);
  const { showDetailsRight } = useVisningstypContext();
  const isFloatingMenuVisible = useIsFloatingMenuVisible();
  const width = useWidth();
  const [expanded, setExpanded] = useState(false);
  const [confirmAction, setConfirmAction] = useState<IEditMenuAction | null>(
    null
  );
  const onChange = useCallback(
    (_: React.SyntheticEvent<Element, Event>, newExpanded: boolean) => {
      setExpanded(newExpanded);
    },
    []
  );
  const { rollbarId: globalRollbarId, setRollbarId: setGlobalRollbarId } =
    useRollbarId();

  if (formContext) {
    const { error, openErrorDialog, closeErrorDialog, editMenuActions } =
      formContext;

    const actionParams = {
      businessDataFormContext: formContext,
      editMenuOnCancel: props.onCancel,
      selectDetail: detailSelectionContext.selectDetail,
    };

    const getConfirmButtonCaption = (action: IEditMenuAction) =>
      typeof action.confirmButtonCaption === 'function'
        ? action.confirmButtonCaption(actionParams)
        : action.confirmButtonCaption;

    const getConfirmDialogCaption = (action: IEditMenuAction) =>
      typeof action.confirmDialogCaption === 'function'
        ? action.confirmDialogCaption(actionParams)
        : action.confirmDialogCaption;

    const getConfirmDialogMessage = (action: IEditMenuAction) =>
      typeof action.confirmDialogMessage === 'function'
        ? action.confirmDialogMessage(actionParams)
        : action.confirmDialogMessage;

    const onActionClick = (action: IEditMenuAction) => {
      const confirmButtonCaption = getConfirmButtonCaption(action);
      const confirmDialogCaption = getConfirmDialogCaption(action);
      const confirmDialogMessage = getConfirmDialogMessage(action);

      if (
        confirmButtonCaption &&
        confirmDialogCaption &&
        confirmDialogMessage
      ) {
        setConfirmAction(action);
        return;
      }

      action.onClick(actionParams);
    };

    const functionMenuActions: IEditMenuAction[] =
      props.functionMenuItems
        ?.filter(item => !item.hidden)
        ?.map(item => ({
          id: item.id,
          label: item.caption,
          onClick: () => {
            item.onClick?.();
          },
          isDisabled: () =>
            item.disabled ? item.disabledReason ?? true : false,
          icon: item.iconType ?? 'circle',
          confirmButtonCaption: item.confirmButtonCaption,
          confirmDialogCaption: item.confirmDialogCaption,
          confirmDialogMessage: item.confirmDialogMessage,
        })) ?? [];
    // If in edit mode, show editMenuActions (or default save/cancel if not provided), otherwise show function menu actions
    const menuItems: IEditMenuAction[] = formContext.formState.editMode
      ? editMenuActions && editMenuActions.length > 0
        ? editMenuActions.filter(action => !action.isHidden?.(actionParams))
        : [defaultEditMenuCancelButton, defaultEditMenuSaveButton]
      : functionMenuActions;
    // Limit width of direct actions to 3 on mobile and 5 on desktop, rest goes into "mer" menu
    const { directActions, otherActions } = splitEditMenuActions(
      menuItems ?? [],
      width === 'xs' ? 3 : 5
    );
    // If in edit mode, instead add function menu actions to the "mer" menu
    const moreActions: IEditMenuAction[] = [
      ...(otherActions ?? []),
      ...(formContext.formState.editMode ? functionMenuActions : []),
    ];

    const moreMenuItems = moreActions?.map(otherAction => {
      const hidden = otherAction.isHidden?.(actionParams);
      const disabled = otherAction.isDisabled?.(actionParams);
      const label =
        typeof otherAction.label === 'function'
          ? otherAction.label(actionParams)
          : otherAction.label;

      if (hidden) {
        return undefined;
      }
      return (
        <FunctionItem
          iconType={otherAction?.icon}
          onClick={() => {
            otherAction.onClick(actionParams);
          }}
          disabled={Boolean(disabled)}
          disabledReason={typeof disabled !== 'boolean' ? disabled : undefined}
          confirmButtonCaption={getConfirmButtonCaption(otherAction)}
          confirmDialogCaption={getConfirmDialogCaption(otherAction)}
          confirmDialogMessage={getConfirmDialogMessage(otherAction)}
          key={otherAction.id}
        >
          {label}
        </FunctionItem>
      );
    });

    const moreButton = moreActions && moreActions?.length > 0 && (
      <MenuButton
        iconColor="inherit"
        tooltip={'Mer'}
        icon={<EDIcon color="inherit" iconType="more_vert" size="medium" />}
        noClickPropagation
      >
        {moreMenuItems}
      </MenuButton>
    );

    const buttonsXSOverflow = editMenuActions && editMenuActions.length > 3;
    const versionError = Boolean(
      error?.graphQLErrors?.some(
        gqlError => gqlError.extensions?.ExceptionClass === 'EDVersionException'
      )
    );

    const errorDialogs = (
      <>
        {error && versionError && (
          <Dialog
            open={openErrorDialog}
            title={'Kunde inte spara'}
            onClose={() => closeErrorDialog(false)}
            footerActions={[
              {
                label: 'Avbryt',
                onClick: () => closeErrorDialog(false),
              },
              {
                label: 'Ladda om',
                onClick: () => {
                  formContext
                    .refetchData?.()
                    .then(result => {
                      if (!result.error) {
                        formContext.formStateDispatch({ type: 'RESET' });
                      }
                      closeErrorDialog(false);
                    })
                    .catch(() => {
                      closeErrorDialog(false);
                    });
                },
                buttonProps: {
                  disabled:
                    formContext.formState.loading ||
                    formContext.mutationLoading,
                },
              },
            ]}
            classes={{
              paper: classes.dialogPaper,
              content: classes.content,
            }}
            isErrorDialog
          >
            <EDTypography variant="body2">{`${formContext.model.RootType.SBLabel} har ändrats i en annan rutin eller av en annan användare och kan därför inte ändras.`}</EDTypography>
          </Dialog>
        )}
        {error && !versionError && (
          <Dialog
            open={openErrorDialog}
            title={'Internt fel'}
            onClose={() => {
              closeErrorDialog(false);
              setGlobalRollbarId(null);
            }}
            footerActions={[
              {
                label: 'Stäng',
                onClick: () => closeErrorDialog(false),
              },
            ]}
            classes={{
              paper: classes.dialogPaper,
              content: classes.content,
            }}
          >
            <ErrorText displayRollbarId={globalRollbarId} />
            <EDTypography color="primary" variant="body2">
              Teknisk information
            </EDTypography>
            <EDBox>
              <EDControlledExpansionPanel
                expanded={expanded}
                onChange={onChange}
                lightBackgroundColor
                overflowHidden
                label={
                  <EDTypography className={classes.errorText} variant="body2">
                    {expanded ? '' : error.message}
                  </EDTypography>
                }
              >
                <EDTypography
                  className={classes.errorTextExpanded}
                  variant="body2"
                >
                  {error.message}
                </EDTypography>
              </EDControlledExpansionPanel>
              <EDErrorTimeStamp
                bottom="52px"
                right="0px"
                borderTopLeftRadius="5px"
                positionFixed={true}
                setBackground={true}
              />
            </EDBox>
          </Dialog>
        )}
      </>
    );

    if (isFloatingMenuVisible && !showDetailsRight) {
      const floatingActions = directActions;

      const floatingMoreButton = moreActions && moreActions.length > 0 && (
        <EDLabelMenu
          noClickPropagation
          clickableElement={onClick => (
            <FloatingMenuButton
              onClick={onClick}
              title="Mer"
              data-testid="more"
            >
              <EDIcon iconType="more_vert" color="inherit" />
              <FloatingMenuButtonLabel>Mer</FloatingMenuButtonLabel>
            </FloatingMenuButton>
          )}
        >
          {moreMenuItems}
        </EDLabelMenu>
      );

      const detaljVyCollectionName = findRoutineDetaljVyCollectionName(
        props.rutinKod
      );
      const hideArrows = Boolean(detaljVyCollectionName);

      return (
        <>
          {errorDialogs}
          <FloatingMenu>
            {floatingMoreButton}
            {floatingActions?.map(action => {
              const hidden = action.isHidden?.(actionParams);
              const disabled = action.isDisabled?.(actionParams);
              const label =
                typeof action.shortLabel === 'function'
                  ? action.shortLabel(actionParams)
                  : action.shortLabel ??
                    (typeof action.label === 'function'
                      ? action.label(actionParams)
                      : action.label);

              if (hidden) return null;

              return (
                <FloatingMenuButton
                  key={action.id}
                  onClick={() => onActionClick(action)}
                  disabled={Boolean(disabled)}
                  title={typeof disabled !== 'boolean' ? disabled : undefined}
                  data-testid={action.id}
                  primary={action.primary && !action.flat}
                >
                  {action.icon && (
                    <EDIcon iconType={action.icon} color="inherit" />
                  )}
                  <FloatingMenuButtonLabel>{label}</FloatingMenuButtonLabel>
                </FloatingMenuButton>
              );
            })}
            {!hideArrows && (
              <FloatingMenuSlot span={2}>
                <EDBox
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    justifyContent: 'center',
                    gap: '2px',
                    width: '100%',
                    color: themeColors.customGlass.buttonText,
                  }}
                >
                  <EDBox
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                      width: '100%',
                    }}
                  >
                    <EDBox sx={{ display: 'flex', justifyContent: 'center' }}>
                      <FloatingMenuButton
                        onClick={() =>
                          detailSelectionContext.selectPreviousDetail?.()
                        }
                        disabled={!detailSelectionContext.selectPreviousDetail}
                      >
                        <EDIcon iconType={'chevron_left'} color="inherit" />
                      </FloatingMenuButton>
                    </EDBox>

                    <EDBox sx={{ display: 'flex', justifyContent: 'center' }}>
                      <FloatingMenuButton
                        onClick={() =>
                          detailSelectionContext.selectNextDetail?.()
                        }
                        disabled={!detailSelectionContext.selectNextDetail}
                      >
                        <EDIcon iconType={'chevron_right'} color="inherit" />
                      </FloatingMenuButton>
                    </EDBox>
                  </EDBox>
                  {detailSelectionContext.currentItem &&
                    detailSelectionContext.totalItems && (
                      <EDBox
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          minWidth: 0,
                        }}
                      >
                        <FloatingMenuButtonLabel>{`${
                          (
                            detailSelectionContext.currentItem.toString() +
                            detailSelectionContext.totalItems.toString()
                          ).length <= 4
                            ? 'Visar '
                            : ''
                        }${detailSelectionContext.currentItem} av ${
                          detailSelectionContext.totalItems
                        }`}</FloatingMenuButtonLabel>
                      </EDBox>
                    )}
                </EDBox>
              </FloatingMenuSlot>
            )}
          </FloatingMenu>
          <EDConfirmationDialog
            title={
              confirmAction ? getConfirmDialogCaption(confirmAction) ?? '' : ''
            }
            confirmCaption={
              confirmAction ? getConfirmButtonCaption(confirmAction) ?? '' : ''
            }
            open={Boolean(confirmAction)}
            onClose={() => setConfirmAction(null)}
            onConfirm={() => {
              setConfirmAction(null);
              confirmAction?.onClick(actionParams);
            }}
          >
            {confirmAction ? getConfirmDialogMessage(confirmAction) ?? '' : ''}
          </EDConfirmationDialog>
        </>
      );
    }

    return (
      <DialogScrollOverlay
        classes={{
          container: cx(classes.overlay, {
            [classes.overlayNoPaddingXS]: buttonsXSOverflow,
          }),
        }}
      >
        {errorDialogs}
        <div
          className={cx(classes.actionButtonsContainer, {
            [classes.actionButtonsContainerSpaceAroundXS]: buttonsXSOverflow,
          })}
        >
          {objektVyContext[0].sidaVidSida &&
            objektVyContext[0].canAdjustSidaVidSida &&
            width !== 'xs' && <BusinessDataFormEditMenuAdjustSideWidths />}
          {moreButton}
          {directActions?.map(directEditMenuAction => {
            return (
              <BusinessDataFormEditMenuActionButton
                editMenuAction={directEditMenuAction}
                onCancel={props.onCancel}
                key={directEditMenuAction.id}
              />
            );
          })}
        </div>
      </DialogScrollOverlay>
    );
  }
  return (
    <InternalError
      id="ac8fcf73-802e-445a-8749-710770692d49"
      message="BusinessDataFormEditOverlay utanför formcontext"
    />
  );
};

export default BusinessDataFormEditMenu;
